
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface PracticeProps {
  currentLevel: number;
  currentSong: number;
  setCurrentSong: (song: number) => void;
  currentLanguage: string;
  translate: boolean;
  examples: Example[];
  top10Songs: Song[];
  initialIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
}

interface Example {
  id: string;
  level: number;
  songId: string;
  imageFull: string;
  imageUrl: string;
  index: number;
  twisted: string[];
  text: string;
  imagePermanentLink: string;
  translation: Record<string, string>;
}

interface Song {
  id: string;
  name: string;
  artist: string;
  position: number;
  yt: string[];
  ytIndex: number;
  translation: Record<string, string>;
}

const Practice: React.FC<PracticeProps> = ({
  currentLevel,
  currentSong,
  setCurrentSong,
  currentLanguage,
  translate,
  examples,
  top10Songs,
  initialIndex,
  onNext,
  onPrevious
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex ?? 0);
  const [isListening, setIsListening] = useState(false);
  const [feedback, setFeedback] = useState<string>("");
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const recognitionRef = useRef<any>(null);
  const navigate = useNavigate();

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Reset index when level or initialIndex changes
  useEffect(() => {
    setCurrentIndex(initialIndex ?? 0);
    setFeedback("");
  }, [currentLevel, initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, examples.length, onNext, onPrevious]);

  const goToNext = () => {
    if (currentIndex < examples.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setFeedback("");
    } else if (onNext) {
      onNext();
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setFeedback("");
    } else if (onPrevious) {
      onPrevious();
    }
  };

  // Touch handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Text-to-speech
  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      window.speechSynthesis.speak(utterance);
    }
  };

  // Speech recognition
  const startListening = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
      setFeedback("Listening...");
    };

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript.toLowerCase().trim();
      const currentExample = examples[currentIndex];
      const targetText = currentExample.text.toLowerCase().trim();

      if (transcript === targetText || transcript.replace(/[.,!?]/g, '') === targetText.replace(/[.,!?]/g, '')) {
        setFeedback(`✓ Correct! It is "${currentExample.text}"`);
        speakText(`Correct! It is ${currentExample.text}`);
      } else {
        setFeedback(`✗ You said: "${transcript}". Try again!`);
      }
      setIsListening(false);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error', event.error);
      setIsListening(false);
      setFeedback("Error with speech recognition. Try again.");
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [examples, currentIndex]);

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  // Get current song info
  const getCurrentSongInfo = () => {
    if (examples.length === 0 || !examples[currentIndex]) return null;
    const songId = examples[currentIndex].songId;
    return top10Songs.find(song => song.id === songId);
  };

  const songInfo = getCurrentSongInfo();


  // Update currentSong when navigating to a different songId
  useEffect(() => {
    if (examples.length > 0 && currentIndex >= 0 && currentIndex < examples.length) {
      const currentExample = examples[currentIndex];
      if (currentExample.songId !== top10Songs[currentSong].id) {
        setCurrentSong(top10Songs.findIndex(song => song.id === currentExample.songId));
      }
    }
  }, [currentIndex, examples, currentSong, setCurrentSong]);

  /*
  // Navigate to first card of currentSong when currentSong changes
  useEffect(() => {
    if (!examples[currentIndex]) return;
    if (examples[currentIndex].songId === top10Songs[currentSong].id) return;

     const firstIndex = examples.findIndex(ex => ex.songId === top10Songs[currentSong].id);
     if (firstIndex !== -1) {
       setCurrentIndex(firstIndex);
     }
  }, [currentSong, examples, currentIndex, top10Songs]);*/

  if (examples.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: 'white',
        fontSize: '1.5rem',
        textShadow: '0 2px 4px rgba(0,0,0,0.5)'
      }}>
        Loading examples...
      </div>
    );
  }

  const currentExample = examples[currentIndex];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        padding: 'clamp(10px, 2vh, 20px)',
        position: 'relative',
        backgroundColor: 'transparent',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Main Card */}
      <div style={{
        maxWidth: '800px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(8px, 2vh, 20px)',
        margin: 'auto'
      }}>

        {/* Image */}
        <div className="example-image-container">
          <img
            src={currentExample.imageUrl}
            alt={currentExample.text}
            className="example-image"
          />
        </div>

        {/* Text and Translation */}
        <div style={{
          backgroundColor: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(10px)',
          padding: 'clamp(12px, 2.5vh, 24px)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h2 style={{
            color: 'white',
            fontSize: 'clamp(1.2rem, 5vh, 2.5rem)',
            margin: '0 0 8px 0',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)'
          }}>
            {currentExample.text}
          </h2>

          {translate && currentExample.translation[currentLanguage] && (
            <p style={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              margin: 0,
              fontStyle: 'italic'
            }}>
              {currentExample.translation[currentLanguage]}
            </p>
          )}
        </div>

        {/* Controls */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => speakText(currentExample.text)}
            style={{
              padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 3vw, 24px)',
              fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
              backgroundColor: 'rgba(59, 130, 246, 0.8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            🔊 Play
          </button>

          <button
            onClick={isListening ? stopListening : startListening}
            style={{
              padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 3vw, 24px)',
              fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
              backgroundColor: isListening ? 'rgba(239, 68, 68, 0.8)' : 'rgba(16, 185, 129, 0.8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            {isListening ? '⏹️ Stop' : '🎤 Speak'}
          </button>
        </div>

        {/* Feedback */}
        {feedback && (
          <div style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(10px)',
            padding: 'clamp(8px, 1.5vh, 16px)',
            borderRadius: '8px',
            textAlign: 'center',
            color: 'white',
            fontSize: 'clamp(0.9rem, 1.8vh, 1.1rem)'
          }}>
            {feedback}
          </div>
        )}

        {/* Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={goToPrevious}
            disabled={currentIndex === 0 && !onPrevious}
            style={{
              padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 2vw, 20px)',
              fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
              backgroundColor: (currentIndex === 0 && !onPrevious) ? 'rgba(100,100,100,0.5)' : 'rgba(100,100,100,0.8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: (currentIndex === 0 && !onPrevious) ? 'not-allowed' : 'pointer',
              backdropFilter: 'blur(10px)',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            ← Previous
          </button>

          <span style={{
            color: 'white',
            fontSize: '1rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            backgroundColor: 'rgba(0,0,0,0.3)',
            padding: '8px clamp(8px, 2vw, 16px)',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)',
            whiteSpace: 'nowrap'
          }}>
            {currentIndex + 1} / {examples.length}
          </span>

          <button
            onClick={goToNext}
            disabled={currentIndex === examples.length - 1 && !onNext}
            style={{
              padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 2vw, 20px)',
              fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
              backgroundColor: (currentIndex === examples.length - 1 && !onNext) ? 'rgba(100,100,100,0.5)' : 'rgba(100,100,100,0.8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: (currentIndex === examples.length - 1 && !onNext) ? 'not-allowed' : 'pointer',
              backdropFilter: 'blur(10px)',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            Next →
          </button>
        </div>

        {/* Song Info */}
        {songInfo && (
          <div style={{
            backgroundColor: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(10px)',
            padding: '8px 12px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(8px, 2vw, 12px)',
            flexWrap: 'wrap'
          }}>
            {/* YouTube Thumbnail as round button linking to Lyrics */}
            {songInfo.yt && songInfo.yt[songInfo.ytIndex] && (
              <button
                onClick={() => {
                  const idx = top10Songs.findIndex(s => s.id === songInfo.id);
                  if (idx !== -1) setCurrentSong(idx);
                  navigate('/lyrics');
                }}
                title="Open Lyrics"
                aria-label="Open Lyrics"
                style={{
                  width: '56px',
                  height: '56px',
                  padding: 0,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  background: 'transparent'
                }}
              >
                <img
                  src={`https://img.youtube.com/vi/${songInfo.yt[songInfo.ytIndex]}/default.jpg`}
                  alt={songInfo.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </button>
            )}

            <div style={{ flex: 1, minWidth: '150px' }}>
              <div style={{
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '600',
                marginBottom: '4px'
              }}>
                #{songInfo.position} - {songInfo.name}
              </div>
              <div style={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.85rem'
              }}>
                {songInfo.artist}
              </div>
              {translate && songInfo.translation && songInfo.translation[currentLanguage] && (
                <div style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.8rem',
                  fontStyle: 'italic',
                  marginTop: '4px'
                }}>
                  {songInfo.translation[currentLanguage]}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Practice;
